import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import moment from 'moment';

import { types as sdkTypes } from '../../util/sdkLoader';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useLocationBasedPhoneNumber } from '../../hooks/useLocationBasedPhoneNumber';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import {
  convertMoneyToDecimalJS,
  convertMoneyToNumber,
  convertPrice,
  convertUnitToSubUnit,
  decimalJSToNumber,
  formatMoney,
  unitDivisor,
} from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { generateCitySlug, generateStoreSlug } from '../../util/seo';
import {
  getCustomPackagePrice,
  sortPackages,
  userDisplayNameAsString,
} from '../../util/data';
import {
  getCartItemById,
  getCartItemByListingId,
  computeInitialValuesFromCartItem,
  computeInitialValuesFromDraft,
  hasRequiredMonthlyTimeSlotsForDraft,
  getMissingMonthRangesForDraft,
} from '../../util/cartPrefillHelpers';
import {
  BOOKING_CATEGORY_DAILY,
  BOOKING_CATEGORY_OVERNIGHT,
  BOOKING_CATEGORY_HOURLY,
  BOOKING_CATEGORY_MINUTE,
  getBookingCategories,
  getBookingCategoryPriceKeys,
  getDefaultCategory,
} from '../../util/booking';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  BookingCategoryToggle,
  Button,
  DisabledCtaWithTooltip,
  ModalInMobile,
  PrimaryButton,
  H2,
} from '../../components';

import { loadRentalDefaults } from '../../util/rentalDefaults';
import { getListingUnitCount } from '../../util/listingInventory';
import css from './OrderPanel.module.css';
import {
  DELIVERY_FEE,
  PICKUP_AT_OWNER_LOCATION,
  PICKUP_FEE,
  REQUIRED_UPFRONT,
  REQUIRED_UPFRONT_OPTIONAL,
  USAGE_MULTIPLE,
} from '../../util/constants';
import Decimal from 'decimal.js';
import { organizeFees } from './OrderPanelHelpers';
import { parseDateRangeFromURL } from '../../util/dateRangeHelpers';

const { Money } = sdkTypes;
// import { staticExchangeRates } from '../../config/configStripe';

const BookingTimeForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm'
  )
);
const BookingDatesForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(
    /* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

export const PER_DAY = 'perDayPrice';
export const PER_MINUTE = 'perMinutePrice';
export const PER_HOUR = 'perHourPrice';
export const TWO_DAYS = 'twoDaysPrice';
export const THREE_DAYS = 'threeDaysPrice';
export const FOUR_DAYS = 'fourDaysPrice';
export const FIVE_DAYS = 'fiveDaysPrice';
export const WEEKLY_PRICE = 'oneWeekPrice';
export const OVERNIGHT_PRICE = 'overnightPrice';
export const TWENTY_EIGHT_DAYS = 'twentyEightDaysPrice';
export const DAY_PACKAGES = [
  PER_DAY,
  TWO_DAYS,
  THREE_DAYS,
  FOUR_DAYS,
  FIVE_DAYS,
  WEEKLY_PRICE,
  OVERNIGHT_PRICE,
  TWENTY_EIGHT_DAYS,
];

const PACKAGE_ORDER = [
  { key: PER_DAY, days: 1 },
  { key: TWO_DAYS, days: 2 },
  { key: THREE_DAYS, days: 3 },
  { key: FOUR_DAYS, days: 4 },
  { key: FIVE_DAYS, days: 5 },
  { key: WEEKLY_PRICE, days: 7 },
  { key: TWENTY_EIGHT_DAYS, days: 28 },
];

const isWindowDefined = typeof window !== 'undefined';
const isDesktopLayout = isWindowDefined && window.innerWidth > MODAL_BREAKPOINT;

const estimatedTotalPrice = (lineItems, marketplaceCurrency) => {
  const numericTotalPrice = lineItems.reduce((sum, lineItem) => {
    const numericPrice = convertMoneyToNumber(lineItem.lineTotal);
    return new Decimal(numericPrice).add(sum);
  }, new Decimal(0));

  // All the lineItems should have same currency so we can use the first one to check that
  // In case there are no lineItems we use currency from config.js as default
  const currency =
    lineItems[0] && lineItems[0].unitPrice
      ? lineItems[0].unitPrice.currency
      : marketplaceCurrency;

  return new Money(
    convertUnitToSubUnit(numericTotalPrice.toNumber(), unitDivisor(currency)),
    currency
  );
};

/**
 * Get average daily price for a booking period
 * @param {*} lineItems
 * @param {*} bookingDates
 * @returns avgPrice
 */
const getLineItemsDayAvgPrice = (lineItems, bookingDates) => {
  const { bookingStart, bookingEnd } = bookingDates;

  // Calculate number of days
  const units = moment(bookingEnd).diff(moment(bookingStart), 'days');
  const itemLineItem = lineItems.find(
    i =>
      Array.isArray(i.includeFor) &&
      i.includeFor.includes('customer') &&
      i.includeFor.includes('provider')
  );

  const { lineTotal } = itemLineItem;
  const { amount, currency } = lineTotal;

  // Number() guards against string amounts from same-currency convertPrice passthrough.
  const avgAmount = Number(amount) / units;
  const avgPrice = new Money(avgAmount, currency);
  return avgPrice;
};

/**
 * Get average daily price for a package
 * @param {*} price
 * @param {*} package
 */
const getPackageAvgPrice = (price, selectedPackage, customPackage) => {
  if (price && price.amount) {
    // Number() normalizes string amounts from publicData (e.g. "4500") before division.
    // Without it, JS coercion handles division correctly but passing a string into
    // new Money() has the same root cause as the capping-loop string comparison bug.
    const numericAmount = Number(price.amount);

    if (customPackage && customPackage.days) {
      return new Money(numericAmount / customPackage.days, price.currency);
    }

    if (customPackage && customPackage.hours) {
      return new Money(numericAmount / customPackage.hours, price.currency);
    }

    if (customPackage && customPackage.minutes) {
      return new Money(numericAmount / customPackage.minutes, price.currency);
    }

    switch (selectedPackage) {
      case PER_DAY:
        return price;
      case TWO_DAYS:
        return new Money(numericAmount / 2, price.currency);
      case THREE_DAYS:
        return new Money(numericAmount / 3, price.currency);
      case FOUR_DAYS:
        return new Money(numericAmount / 4, price.currency);
      case FIVE_DAYS:
        return new Money(numericAmount / 5, price.currency);
      case WEEKLY_PRICE:
        return new Money(numericAmount / 7, price.currency);
      case TWENTY_EIGHT_DAYS:
        return new Money(numericAmount / 28, price.currency);
      default:
        return price;
    }
  }
};

/**
 * Convert the price in current user's currency
 * @param {*} param
 * @returns converted price.
 */
const getConvertedPrice = ({
  packageAvgPrice,
  exchangeRatesReady,
  exchangeRates,
  currentUserCurrency,
}) => {
  const convertedPrice =
    !!packageAvgPrice && !!exchangeRatesReady
      ? convertPrice(
          exchangeRates,
          packageAvgPrice.amount,
          packageAvgPrice.currency,
          currentUserCurrency
        )
      : null;
  return convertedPrice;
};

/**
 * Number of days a package/customPackage represents, for the "$X for N days" pill text.
 * Returns null for non-day-based packages (hourly/minute), where no day count applies.
 */
/**
 * Duration of a sub-day (hour/minute) booking, for the price pill's "for 45 minutes" /
 * "for 3 hours" label.
 *
 * Manual QA: the pill previously had no notion of sub-day duration at all -
 * getPackageDayCount deliberately returns null for hour/minute packages, so a
 * time-based booking fell through to the generic OrderPanel.perUnit label and rendered
 * as "$75.00 - Per minute": the total for the whole booking, captioned as if it were
 * the per-minute rate. handleDateTimeRangeChange already computes and stores the real
 * duration on customPackage ({ minutes } or { hours }, alongside the correctly
 * multiplied price), so this just surfaces what's already there and lets the pill read
 * "$75.00 for 5 minutes" - the same shape as the day flow's "$45.00 for 2 days".
 */
/**
 * RAS-2520: how many nights an overnight booking covers, for the price pill's
 * "rental · 3 nights" label. Mirrors getPackageSubDayDuration below - handleDateRangeChange
 * already stores the count on customPackage alongside the correctly multiplied price, so
 * this only surfaces what's there.
 */
const getPackageNightCount = (selectedPackage, customPackage) => {
  if (customPackage && customPackage.nights) {
    return customPackage.nights;
  }
  // Overnight selected but no range picked yet: a single night is the implicit unit.
  if (!customPackage && selectedPackage === OVERNIGHT_PRICE) {
    return 1;
  }
  return null;
};

const getPackageSubDayDuration = customPackage => {
  if (!customPackage) {
    return null;
  }
  if (customPackage.minutes) {
    return { count: customPackage.minutes, unit: 'minutes' };
  }
  if (customPackage.hours) {
    return { count: customPackage.hours, unit: 'hours' };
  }
  return null;
};

const getPackageDayCount = (selectedPackage, customPackage) => {
  if (customPackage && customPackage.days) {
    return customPackage.days;
  }
  if (customPackage && (customPackage.hours || customPackage.minutes)) {
    return null;
  }
  // RAS-2520: an overnight booking is counted in nights, so it never contributes a DAY
  // count - getPackageNightCount below owns its label. Without this, a 3-night booking
  // fell through to the OVERNIGHT_PRICE branch and rendered "rental · 1 day".
  if (customPackage && customPackage.nights) {
    return null;
  }
  if (selectedPackage === OVERNIGHT_PRICE) {
    return 1;
  }
  const found = PACKAGE_ORDER.find(p => p.key === selectedPackage);
  return found ? found.days : null;
};

/**
 * Min/max per-unit average price across the package tiers available on the listing
 * WITHIN ONE booking category, for the pre-selection price-range pill ("$14 - $30 per
 * day"). Reuses the existing getPackageAvgPrice - no new pricing data, purely derived
 * from pricePackages that OrderPanel already computes.
 *
 * RAS-2520: previously hardcoded to DAY_PACKAGES. Now parameterised by category so the
 * pill spans only the tiers of whichever category the toggle has active - passing
 * BOOKING_CATEGORY_DAILY reproduces the old behaviour exactly (DAILY's key list IS
 * DAY_PACKAGES, see PRICE_KEYS_BY_BOOKING_CATEGORY), which is what keeps single-tier
 * day listings byte-for-byte unchanged. HOURLY/MINUTE each have a single tier, so their
 * "range" is always a single price - handled by the isRange check at the call site,
 * which already existed for single-tier day listings.
 */
const getCategoryPriceRange = ({
  pricePackages,
  category,
  exchangeRatesReady,
  exchangeRates,
  currentUserCurrency,
}) => {
  if (!pricePackages || !category) return null;

  const dayAvgPrices = getBookingCategoryPriceKeys(pricePackages, category)
    .map(key => ({ key, price: pricePackages[key] }))
    .map(({ key, price }) =>
      getConvertedPrice({
        packageAvgPrice: getPackageAvgPrice(price, key, null),
        exchangeRatesReady,
        exchangeRates,
        currentUserCurrency,
      })
    )
    .filter(Boolean);

  if (dayAvgPrices.length === 0) return null;

  const sorted = [...dayAvgPrices].sort((a, b) => a.amount - b.amount);
  return { min: sorted[0], max: sorted[sorted.length - 1] };
};

/**
 * RAS-2520: which package tier to select within a booking category when there are no
 * dates picked yet - the listing's own defaultPriceKey when that tier belongs to the
 * category, otherwise the shortest tier the listing sells in it
 * (getBookingCategoryPriceKeys returns them in canonical tier order).
 *
 * This is only ever an interim value: as soon as the renter picks a range,
 * handleDateRangeChange/handleDateTimeRangeChange derive the real package from the
 * chosen duration, exactly as they always have.
 */
const pickPackageForCategory = (prices, category, defaultPriceKey) => {
  if (!category) return null;
  const keys = getBookingCategoryPriceKeys(prices, category);
  return keys.includes(defaultPriceKey) ? defaultPriceKey : keys[0] || null;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (
  isOwnListing,
  isClosed,
  history,
  location,
  selectedPackage
) => {
  if (isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({
      ...parse(search),
      orderOpen: true,
      package: selectedPackage,
    })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location,
  selectedPackage
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () =>
        openOrderModal(
          isOwnListing,
          isClosed,
          history,
          location,
          selectedPackage
        );
};

const dateFormattingOptions = {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
};

const getSavingsHint = (range, pricePackages, intl) => {
  const { startDate, endDate } = range;
  const days = moment(endDate).diff(moment(startDate), 'days');
  const packagesMapping = {
    1: PER_DAY,
    2: TWO_DAYS,
    3: THREE_DAYS,
    4: FOUR_DAYS,
    5: FIVE_DAYS,
    7: WEEKLY_PRICE,
    28: TWENTY_EIGHT_DAYS,
  };
  const availablePackages = Object.keys(pricePackages || {});
  const selectedPackage = packagesMapping[days];
  let currentPerDay = null;
  if (selectedPackage && availablePackages.includes(selectedPackage)) {
    currentPerDay = getPackageAvgPrice(
      pricePackages[selectedPackage],
      selectedPackage
    );
  } else {
    const customPackage = getCustomPackagePrice(days, pricePackages);
    currentPerDay = getPackageAvgPrice(customPackage, selectedPackage, {
      days,
    });
  }

  let bestSavings = null;
  for (let { key, days: pkgDays } of PACKAGE_ORDER) {
    const pkg = pricePackages[key];
    if (!pkg || pkgDays <= days) continue;
    const pkgPerDay = getPackageAvgPrice(pkg, key);
    if (pkgPerDay && currentPerDay && pkgPerDay.amount < currentPerDay.amount) {
      const saving = currentPerDay.amount - pkgPerDay.amount;
      if (!bestSavings || saving > bestSavings.savingPerDay) {
        bestSavings = {
          betterPackageKey: key,
          betterPackageDays: pkgDays,
          savingPerDay: saving,
          additionalDays: pkgDays - days,
          formattedSaving: formatMoney(
            intl,
            new Money(saving, currentPerDay.currency)
          ),
          newPerDayRate: pkgPerDay.amount,
        };
      }
    }
  }

  return bestSavings;
};

// Derive package purely from duration; duration is the source of truth.
const derivePackageFromDuration = (start, end, pricePackages) => {
  if (!start || !end || !pricePackages) return null;

  const totalDays = moment(end).diff(moment(start), 'days');
  const totalHours = moment(end).diff(moment(start), 'hours', true);

  if (totalHours <= 0) return null;

  // Day-based
  if (totalDays >= 1) {
    if (pricePackages[TWENTY_EIGHT_DAYS] && totalDays >= 28)
      return TWENTY_EIGHT_DAYS;
    if (pricePackages[WEEKLY_PRICE] && totalDays >= 7) return WEEKLY_PRICE;
    if (pricePackages[FIVE_DAYS] && totalDays >= 5) return FIVE_DAYS;
    if (pricePackages[FOUR_DAYS] && totalDays >= 4) return FOUR_DAYS;
    if (pricePackages[THREE_DAYS] && totalDays >= 3) return THREE_DAYS;
    if (pricePackages[TWO_DAYS] && totalDays >= 2) return TWO_DAYS;
    if (pricePackages[OVERNIGHT_PRICE] && totalDays >= 1)
      return OVERNIGHT_PRICE;
    if (pricePackages[PER_DAY] && totalDays >= 1) return PER_DAY;
  }

  // Hour-based
  if (totalDays < 1 && totalHours >= 1) {
    if (pricePackages[PER_HOUR]) return PER_HOUR;
    if (pricePackages[PER_MINUTE]) return PER_MINUTE;
  }

  // Minute-based
  if (totalDays < 1 && totalHours < 1) {
    if (pricePackages[PER_MINUTE]) return PER_MINUTE;
    if (pricePackages[PER_HOUR]) return PER_HOUR;
  }

  return null;
};

const handleDateRangeChange = (
  range,
  pricePackages,
  setSelectedPackage,
  setCustomPackage,
  currentSelectedPackage
) => {
  // Handle empty form: clear custom and let defaultPriceKey sync pick a package
  if (!range || !range.startDate || !range.endDate) {
    setCustomPackage(null);
    return;
  }

  const { startDate, endDate } = range;
  const days = moment(endDate).diff(moment(startDate), 'days');

  // RAS-2520: respect an explicit overnight selection for ANY number of nights, not
  // just one. This used to be days === 1, on the reasoning that a 2+ day range should
  // auto-upgrade to a multi-day tier - correct while overnight was only ever reachable
  // by accident, but wrong now that Overnight is a category the renter picks
  // deliberately. overnightPrice is a per-NIGHT rate (server/api-util/lineItems.js maps
  // it to the `night` unitType), so N nights is N x the rate, exactly the way the
  // hourly flow multiplies its own per-hour rate - hence a customPackage carrying the
  // night count, mirroring handleDateTimeRangeChange's { hours } / { minutes }.
  //
  // >= 1 also matches what both getRentalPackage implementations (util/data.js and the
  // server's) already do with an explicitly-sent overnightPrice, so the client stops
  // disagreeing with the pricing engine about which package a multi-night booking is.
  if (
    currentSelectedPackage === OVERNIGHT_PRICE &&
    Object.keys(pricePackages || {}).includes(OVERNIGHT_PRICE) &&
    days >= 1
  ) {
    const nightlyRate = pricePackages[OVERNIGHT_PRICE];
    const amount = decimalJSToNumber(convertMoneyToDecimalJS(nightlyRate).mul(days));
    setCustomPackage({
      price: new Money(amount, nightlyRate.currency),
      label: `${days} nights`,
      nights: days,
    });
    return;
  }

  const packagesMapping = {
    1: PER_DAY,
    2: TWO_DAYS,
    3: THREE_DAYS,
    4: FOUR_DAYS,
    5: FIVE_DAYS,
    7: WEEKLY_PRICE,
    28: TWENTY_EIGHT_DAYS,
  };

  const availablePackages = Object.keys(pricePackages || {});

  const selectedPackage = packagesMapping[days];
  if (selectedPackage && availablePackages.includes(selectedPackage)) {
    setSelectedPackage(selectedPackage);
    setCustomPackage(null);
    return;
  }

  // Add the custom package.
  const customPackage = getCustomPackagePrice(days, pricePackages);
  if (customPackage) {
    setCustomPackage({
      price: customPackage,
      label: `${days} days`,
      days,
    });
    // CRITICAL FIX: Set selectedPackage to PER_DAY when customPackage is created for day-based bookings
    // This ensures the correct package is sent to server, preventing pricing errors
    if (availablePackages.includes(PER_DAY)) {
      setSelectedPackage(PER_DAY);
    } else if (availablePackages.includes(TWO_DAYS)) {
      setSelectedPackage(TWO_DAYS);
    } else if (availablePackages.includes(THREE_DAYS)) {
      setSelectedPackage(THREE_DAYS);
    } else if (availablePackages.includes(FOUR_DAYS)) {
      setSelectedPackage(FOUR_DAYS);
    } else if (availablePackages.includes(FIVE_DAYS)) {
      setSelectedPackage(FIVE_DAYS);
    } else if (availablePackages.includes(WEEKLY_PRICE)) {
      setSelectedPackage(WEEKLY_PRICE);
    } else if (availablePackages.includes(TWENTY_EIGHT_DAYS)) {
      setSelectedPackage(TWENTY_EIGHT_DAYS);
    }
  }
};

const handleDateTimeRangeChange = (
  range,
  pricePackages,
  setCustomPackage,
  setSelectedPackage
) => {
  // Handle empty form: clear custom and rely on defaultPriceKey for fallback
  const { bookingStartTime, bookingEndTime } = range || {};
  if (!bookingStartTime || !bookingEndTime) {
    setCustomPackage(null);
    return;
  }

  setCustomPackage(null);

  const minutes = moment(bookingEndTime).diff(
    moment(bookingStartTime),
    'minutes'
  );
  const hours = moment(bookingEndTime).diff(moment(bookingStartTime), 'hours');

  const calculateAmount = (price, quantity) => {
    const amount = convertMoneyToDecimalJS(price);
    const multipliedAmount = amount.mul(quantity);
    return decimalJSToNumber(multipliedAmount);
  };

  // Derive package for sub-day durations
  const derived = derivePackageFromDuration(
    bookingStartTime,
    bookingEndTime,
    pricePackages
  );
  if (derived) {
    setSelectedPackage(derived);
  }

  // Set custom package for minute/hour packages
  if (derived === PER_MINUTE) {
    if (minutes <= 0) { setCustomPackage(null); return; }
    const amount = calculateAmount(pricePackages[PER_MINUTE], minutes);
    setCustomPackage({
      price: new Money(amount, pricePackages[PER_MINUTE].currency),
      label: `${minutes} minutes`,
      minutes,
    });
  } else if (derived === PER_HOUR) {
    if (hours <= 0) { setCustomPackage(null); return; }
    const amount = calculateAmount(pricePackages[PER_HOUR], hours);
    setCustomPackage({
      price: new Money(amount, pricePackages[PER_HOUR].currency),
      label: `${hours} hours`,
      hours,
    });
  }
};

const getCancellationMessage = (cancelation, intl) => {
  if (cancelation === 'easy-going') {
    return intl.formatMessage({
      id: 'OrderPanel.cancellation.easyGoing',
      defaultMessage: 'Full refund with 72+ hours notice',
    });
  } else if (cancelation === 'flexible') {
    return intl.formatMessage({
      id: 'OrderPanel.cancellation.flexible',
      defaultMessage: '50% refund with 72+ hours notice',
    });
  } else if (cancelation === 'strict') {
    return intl.formatMessage({
      id: 'OrderPanel.cancellation.strict',
      defaultMessage: 'No refund after booking is confirmed',
    });
  }
  return cancelation;
};

const getVerificationMessage = (verification, intl) => {
  const testVerification = (verification, value) => {
    return Array.isArray(verification)
      ? verification.includes(value)
      : verification === value;
  };

  if (testVerification(verification, 'not_required')) {
    return intl.formatMessage({
      id: 'OrderPanel.verification.notRequired',
      defaultMessage: 'No Verification Required',
    });
  } else if (testVerification(verification, 'minimum_of_3_successful_rent')) {
    return intl.formatMessage({
      id: 'OrderPanel.verification.minimum3',
      defaultMessage: 'Verification – 3+ successful rentals',
    });
  } else if (testVerification(verification, 'copy_of_drivers_licience')) {
    return intl.formatMessage({
      id: 'OrderPanel.verification.driversLicense',
      defaultMessage: "Verification – Valid driver's license or passport",
    });
  } else if (testVerification(verification, 'visual_id_verification')) {
    return intl.formatMessage({
      id: 'OrderPanel.verification.visualId',
      defaultMessage: 'Visual ID verification required',
    });
  }
  return verification;
};

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    currentUserCurrency,
    selectedPackage,
    customPackage,
    pricePackages,
    datesSelected,
    exchangeRatesReady,
    exchangeRates,
    // RAS-2520: the booking category currently active in the panel, and whether this
    // listing offers more than one at all. Both are absent (undefined) for callers that
    // predate the toggle, which falls back to the pre-toggle defaultPriceKey logic below.
    bookingCategory,
    isMultiCategory,
  } = props;
  const { listingType, unitType, defaultPriceKey } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );

  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(
    price,
    currentUserCurrency,
    intl
  );
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  if (showCurrencyMismatch) {
    return (
      <div className={css.priceContainerInCTA}>
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
        <div className={css.perUnitInCTA}>
          <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        </div>
      </div>
    );
  }

  // Pre-selection (no dates picked yet): a price-range pill across all day-based
  // package tiers, e.g. "$14 - $30 per day". Only meaningful for day-based listings.
  // LOGIC FIX 2 (manual QA): getCategoryPriceRange only checks whether the listing
  // has ANY day-based package configured, not whether the listing's defaultPriceKey
  // actually IS one - a listing offering both perDayPrice and perHourPrice tiers,
  // defaulting to hourly, would otherwise show the day range here instead of its own
  // configured default. defaultPriceKey (from publicData, already passed to this
  // component) takes precedence: skip the day range entirely when the listing's
  // default is hourly/minute-based, falling through unconditionally to the generic
  // single-price pill below, which already has correct per-hour/per-minute labeling
  // (see the OrderPanel.perUnit ICU message) - reused as-is, no new mapping.
  //
  // RAS-2520: which category's tiers this pill should span. On a listing that only sells
  // ONE category (the overwhelming majority - the toggle isn't even rendered there) this
  // deliberately resolves exactly as it did before the toggle existed: DAILY for a
  // day-based listing, and null - i.e. skip the range entirely and fall through to the
  // generic single-price pill below - for an hourly/minute one. Only a genuinely
  // multi-category listing reads the active category instead, so nothing about the
  // single-tier rendering path changes.
  const isTimeBasedDefault = [PER_HOUR, PER_MINUTE].includes(defaultPriceKey);
  const rangeCategory = isMultiCategory
    ? bookingCategory
    : isTimeBasedDefault
    ? null
    : BOOKING_CATEGORY_DAILY;

  if (!datesSelected && rangeCategory) {
    const range = getCategoryPriceRange({
      pricePackages,
      category: rangeCategory,
      exchangeRatesReady,
      exchangeRates,
      currentUserCurrency,
    });
    if (range) {
      // The DAILY branch keeps the exact ListCard.* message ids it has always used, so
      // its rendered copy (and its existing translations) are untouched; the other two
      // categories get their unit/aria copy from ICU-select messages added for them.
      const isDailyRange = rangeCategory === BOOKING_CATEGORY_DAILY;
      const isRange = range.min.amount !== range.max.amount;
      // RAS-2519: same price-block treatment as the search/listing cards (see
      // ListCard.js) - the lead price at 22/800 navy, the high end at 14/700 navy (a
      // real price, so full contrast, never dimmed), the unit at 12/700 grey, and the
      // word "to" rather than an en-dash. Previously this rendered both ends at one
      // size in orange, which gave the eye no direction and, at the footer's width,
      // truncated to "$10.71 - $25.00 per...". aria-label keeps it one spoken phrase.
      return (
        <div
          className={css.pricePill}
          aria-label={
            isRange
              ? intl.formatMessage(
                  {
                    id: isDailyRange
                      ? 'ListCard.priceAriaBrowse'
                      : 'OrderPanel.priceAriaRangeByCategory',
                  },
                  {
                    min: formatMoney(intl, range.min),
                    max: formatMoney(intl, range.max),
                    category: rangeCategory,
                  }
                )
              : intl.formatMessage(
                  {
                    id: isDailyRange
                      ? 'ListCard.priceAriaSingle'
                      : 'OrderPanel.priceAriaSingleByCategory',
                  },
                  { price: formatMoney(intl, range.max), category: rangeCategory }
                )
          }
        >
          <p className={css.pricePillText} aria-hidden="true">
            <span className={css.pricePillLead}>
              {isRange ? formatMoney(intl, range.min) : formatMoney(intl, range.max)}
            </span>
            {isRange ? (
              <span className={css.pricePillSecond}>
                <FormattedMessage
                  id="ListCard.priceRangeTo"
                  values={{ max: formatMoney(intl, range.max) }}
                />
              </span>
            ) : null}
            <span className={css.pricePillUnit}>
              {isDailyRange ? (
                <FormattedMessage id="ListCard.perDayLabel" />
              ) : (
                <FormattedMessage
                  id="OrderPanel.pricePillUnitLabel"
                  values={{ category: rangeCategory }}
                />
              )}
            </span>
          </p>
          <p className={css.pricePillCaption} aria-hidden="true">
            <FormattedMessage id="OrderPanel.basedOnRentalPeriod" />
          </p>
        </div>
      );
    }
  }

  // Dates selected (or a non-day-based listing type): single price pill for the
  // resolved package/duration, e.g. "$30 for 7 days".
  const totalPrice = customPackage
    ? customPackage.price
    : pricePackages
    ? pricePackages[selectedPackage]
    : null;
  const dayCount = getPackageDayCount(selectedPackage, customPackage);
  const nightCount = getPackageNightCount(selectedPackage, customPackage);
  const subDayDuration = getPackageSubDayDuration(customPackage);

  // RAS-2519 State 2 (dates/time selected): "$46 total · 2 days". The amount keeps the
  // headline 22/800 navy treatment used in every state; the trailing phrase is grey
  // because it contains no dollar figure. The word "total" is what stops $46 reading as
  // a daily rate next to the browse state's "/day". Time-based bookings substitute
  // their own unit ("· 3 hours" / "· 5 minutes") for the day count - label only, the
  // amount itself is unchanged and still comes from customPackage/pricePackages exactly
  // as before.
  const formattedTotal = formatMoney(intl, totalPrice || price);

  const totalLabel = nightCount ? (
    <FormattedMessage id="OrderPanel.pricePillRentalNights" values={{ count: nightCount }} />
  ) : dayCount ? (
    <FormattedMessage id="OrderPanel.pricePillRentalDays" values={{ dayCount }} />
  ) : subDayDuration ? (
    <FormattedMessage
      id={
        subDayDuration.unit === 'hours'
          ? 'OrderPanel.pricePillRentalHours'
          : 'OrderPanel.pricePillRentalMinutes'
      }
      values={{ count: subDayDuration.count }}
    />
  ) : DAY_PACKAGES.includes(selectedPackage) ? (
    <FormattedMessage id="OrderPanel.perDayAvgPrice" />
  ) : (
    <FormattedMessage id="OrderPanel.perUnit" values={{ selectedPackage }} />
  );

  // Screen readers should hear one continuous phrase rather than the separately sized
  // fragments that create the hierarchy.
  const totalAriaLabel = nightCount
    ? intl.formatMessage(
        { id: 'OrderPanel.priceAriaRentalNights' },
        { price: formattedTotal, count: nightCount }
      )
    : dayCount
    ? intl.formatMessage(
        { id: 'OrderPanel.priceAriaRentalDays' },
        { price: formattedTotal, dayCount }
      )
    : subDayDuration
    ? intl.formatMessage(
        {
          id:
            subDayDuration.unit === 'hours'
              ? 'OrderPanel.priceAriaRentalHours'
              : 'OrderPanel.priceAriaRentalMinutes',
        },
        { price: formattedTotal, count: subDayDuration.count }
      )
    : undefined;

  return (
    <div
      className={classNames(css.pricePill, css.pricePillSingle)}
      aria-label={totalAriaLabel}
    >
      <p className={css.pricePillText} aria-hidden={totalAriaLabel ? 'true' : undefined}>
        <span className={css.pricePillLead}>{formattedTotal}</span>
        <span className={css.pricePillLabel}>{totalLabel}</span>
      </p>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showCartButton,
    currentUser,
    isCurrentUserListing,
    onAddToCart,
    addToCartInProgress,
    addToCartError,
    cartNotification,
    routeConfiguration,
    onlyPricingSection,
    isVerified,
    reviews,
    showAuthorDetails,
    totalListings,
    breakdownTitle,
    autoFocusBooking,
    isShowCommissionLineItem,
    isMobile,
    usersEmailVerified,
    isCartDrawerOpen,
  } = props;

  // ========== ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS ==========
  const config = useConfiguration();
  const locationBasedPhoneNumber = useLocationBasedPhoneNumber(
    process.env.REACT_APP_TEAM_PHONE_NUMBER
  );

  // Redux selectors
  const { currentUserCurrency } = useSelector(state => state.user);
  const { exchangeRates, fetRatesError, fetchRatesInProgress } = useSelector(
    state => state.exchangeRate
  );
  const cartState = useSelector(state => state.cart);

  // All useState hooks
  const [bookingDates, setBookingDates] = useState(null);
  const [autoFocused, setAutoFocused] = useState(!!autoFocusBooking);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customPackage, setCustomPackage] = useState(null);
  const [scrolling, setScrolling] = useState(false);
  const [savingsHint, setSavingsHint] = useState(null);
  const [datesSelected, setDatesSelected] = useState(false);
  // RAS-2524/2525/2526: mobile-only sticky footer + booking modal (isMobile prop,
  // already threaded in from ListingPageCarousel.js's mobile-only OrderPanel instance
  // but previously unused here). isBookingModalOpen/modalEpoch only matter when
  // isMobile is true - see the mobile branch at the end of this component's render.
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  // Regression fix (manual QA): this used to remount the modal's content (via
  // key={modalEpoch} below) on every closed->open transition - i.e. on every OPEN,
  // not close. That destroyed and recreated BookingDatesForm/BookingTimeForm from
  // scratch every time the user tapped the sticky footer, even when a cart/storefront
  // prefill had already correctly resolved into effectiveInitialValues/
  // prefillCheckComplete beforehand - a fresh mount re-runs whatever mount-time logic
  // those forms use to decide State A vs C, and if that logic reacts to
  // prefillCheckComplete transitioning to true rather than simply being true already,
  // a form that mounts with it already true never observes the transition and falls
  // back to State A regardless of valid initialValues. This is exactly why prefilled
  // dates stopped showing State C after the mobile modal was introduced (before it,
  // BookingDatesForm/BookingTimeForm rendered inline, mounted once, no remount at all).
  // Flipped to remount on CLOSE instead: the epoch starts at 0, so the very first open
  // of a freshly-loaded page reuses the instance that's been mounted (inside
  // ModalInMobile, which keeps content mounted-but-hidden even while closed) since
  // page load - plenty of time for the async prefill to resolve into it via normal
  // prop updates, no remount-timing risk at all. A remount only happens when the
  // modal is subsequently CLOSED, discarding any in-progress, never-Applied calendar
  // selection - bookingDates/datesSelected/selectedPackage above (this component's
  // own state, unaffected by the remount, and only ever updated by confirmed/Applied
  // selections or the prefill ladder, never raw in-progress calendar interaction) still
  // carry the last actually-Applied selection into effectiveInitialValues, so the next
  // open (reusing the instance freshly remounted at close time, again with plenty of
  // lead time before the user reopens it) shows State B/C pre-filled correctly rather
  // than resetting to State A.
  const [modalEpoch, setModalEpoch] = useState(0);
  const wasBookingModalOpenRef = useRef(isBookingModalOpen);
  useEffect(() => {
    if (!isBookingModalOpen && wasBookingModalOpenRef.current) {
      setModalEpoch(e => e + 1);
    }
    wasBookingModalOpenRef.current = isBookingModalOpen;
  }, [isBookingModalOpen]);
  // Nothing closes the booking modal on a successful Rent Now (addToCartHandler's
  // async/instant-resolve paths in ListingPageCarousel.js don't reach back into this
  // component) - it stays mounted with its last-Applied form state while the cart
  // drawer opens on top of it. Closing it here, driven by the cart drawer's own open
  // state, fixes two things at once: the stacking conflict with the drawer (both are
  // Modal-based; this modal needs a very high z-index per its own "nothing below it is
  // clickable" requirement, which would otherwise bury the drawer), and Rent Now
  // silently no-op'ing on a second click (the modal never remounting meant
  // BookingDatesForm's previousFormValues/pendingAddToCartRef self-heal state could get
  // stuck - closing and later reopening remounts it fresh via modalEpoch above).
  useEffect(() => {
    if (isCartDrawerOpen) {
      setIsBookingModalOpen(false);
    }
  }, [isCartDrawerOpen]);
  // Consolidated initial values: cart prefill takes precedence over rental defaults
  const [validatedInitialValues, setValidatedInitialValues] = useState(null);
  const processedCartItemIdRef = useRef(null);
  const processedCartItemByListingRef = useRef(null); // tracks listingId already auto-prefilled from cart
  const processedRentalDefaultsRef = useRef({
    listingId: null,
    processed: false,
  });
  const rentalDefaults = loadRentalDefaults(); // No useMemo needed - localStorage read is cheap

  // Whether the priority effect below (cart prefill > storefront date > rental
  // defaults) has finished checking THIS listing at least once. BookingDatesForm/
  // BookingTimeForm use this to avoid ever showing the "confirmed nothing selected"
  // open calendar before we actually know whether there's something to prefill -
  // cart-prefill data in particular has no synchronous source at all (state.cart.items
  // starts null and is only populated by a fetch dispatched after the initial app
  // render, see src/index.js), so without this gate the calendar would flash open
  // whenever a cart item exists for this listing but rental defaults' own localStorage
  // hint doesn't happen to also cover it (different device/session, cleared storage,
  // or simply never having used that listing's dates elsewhere yet).
  const [prefillCheckComplete, setPrefillCheckComplete] = useState(false);

  useEffect(() => {
    if (autoFocusBooking) {
      setAutoFocused(true);
    }
  }, [autoFocusBooking]);

  // Derived values from listing
  const publicData = listing?.attributes?.publicData || {};
  // One reading of publicData.quantity, shared with the listing header's crawler-facing
  // unit count and the Product JSON-LD, so they can never disagree. See util/listingInventory.js.
  const maxAvailableSeats = getListingUnitCount(publicData);
  const access_to_rentals = publicData?.access_to_rentals;
  const cancelation_policy = publicData?.cancelation_policy;
  const Trust_and_verification = publicData?.Trust_and_verification;
  const isPickupAtOwnerLocation = access_to_rentals?.includes(
    PICKUP_AT_OWNER_LOCATION
  );

  const defaultPriceKey = publicData?.defaultPriceKey
    ? publicData.defaultPriceKey
    : Object.keys(publicData?.prices || {})[0];

  const basePrice = listing?.attributes?.price;
  const prices =
    listing?.attributes?.publicData?.prices ||
    (defaultPriceKey && basePrice
      ? {
          [defaultPriceKey]: {
            amount: basePrice.amount,
            currency: basePrice.currency,
          },
        }
      : {});

  // RAS-2520: the booking categories this listing actually sells, and which one the
  // panel is currently showing. Derived from `prices` (not raw publicData.prices) so a
  // listing that only has a base price + defaultPriceKey still resolves to the single
  // synthesized category above, i.e. no toggle. Purely local state - no Redux, no duck,
  // no extra API calls.
  const availableCategories = getBookingCategories(prices);
  // Falls back to the first category the listing actually sells when defaultPriceKey
  // isn't a recognised price key (mis-configured listing data) - otherwise the toggle
  // would render with no pill active at all and the price pill would lose its range.
  const initialCategory = getDefaultCategory(defaultPriceKey) || availableCategories[0] || null;
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);

  // Memoized price packages - always called.
  const pricePackages = useMemo(() => {
    if (!defaultPriceKey) return null;

    // Build packages in selected currency when rates are available.
    // Fallback to listing currency to avoid false "no price" UI while rates load.
    return sortPackages(
      Object.keys(prices).reduce((packages, key) => {
        const pack = prices[key];
        const moneyInSelectedCurrency =
          pack?.currency === currentUserCurrency
            ? new Money(pack.amount, pack.currency)
            : exchangeRates.length
            ? convertPrice(
                exchangeRates,
                pack.amount,
                pack.currency,
                currentUserCurrency
              )
            : null;

        // If conversion is unavailable, keep original pack so pricing still renders.
        const resolvedMoney =
          moneyInSelectedCurrency || new Money(pack.amount, pack.currency);
        packages[key] = resolvedMoney;
        return packages;
      }, {}),
      defaultPriceKey
    );
  }, [prices, exchangeRates, currentUserCurrency, defaultPriceKey]);

  // Sync selectedPackage with defaultPriceKey
  // CRITICAL FIX: Only sync if we don't have customPackage or validatedInitialValues
  // This prevents overriding the correct package set from initial values
  useEffect(() => {
    if (
      defaultPriceKey &&
      !customPackage &&
      !validatedInitialValues &&
      selectedPackage !== defaultPriceKey
    ) {
      setSelectedPackage(defaultPriceKey);
    }
  }, [defaultPriceKey]);

  // Parse search params once (memoize to avoid re-parsing)
  const searchParams = useMemo(() => parse(location.search), [location.search]);
  const cartItemIdFromUrl = searchParams.cartItemId;
  const isStorefrontFlow = searchParams.from === 'storefront';
  const storefrontDateRange = useMemo(
    () => parseDateRangeFromURL(location.search),
    [location.search]
  );

  // Memoize stable values to optimize dependencies
  const listingId = useMemo(() => listing?.id?.uuid || listing?.id, [
    listing?.id?.uuid,
    listing?.id,
  ]);

  const listingTimeZone = useMemo(
    () => listing?.attributes?.availabilityPlan?.timezone,
    [listing?.attributes?.availabilityPlan?.timezone]
  );

  const listingPublicData = useMemo(() => listing?.attributes?.publicData, [
    listing?.attributes?.publicData,
  ]);

  const allTimeSlotsReady = useMemo(
    () =>
      Object.keys(monthlyTimeSlots).length > 0 &&
      Object.values(monthlyTimeSlots).every(
        ts => ts.fetchTimeSlotsInProgress === false
      ),
    [monthlyTimeSlots]
  );

  // Memoized messages
  const refundMessage = useMemo(
    () => getCancellationMessage(cancelation_policy, intl),
    [cancelation_policy, intl]
  );
  const verificationMessage = useMemo(
    () => getVerificationMessage(Trust_and_verification, intl),
    [Trust_and_verification, intl]
  );

  // Memoized savings message
  const savingPerDayMessage = useMemo(() => {
    if (!savingsHint) return null;
    return (
      <div className={css.savingPerDayMessage}>
        <span className={css.successMessage}>
          <FormattedMessage
            id="OrderPanel.additionalDaysSaving"
            values={{ additionalDays: savingsHint.additionalDays }}
          />
        </span>
        <span className={css.savingPerDayMessageText}>
          <FormattedMessage
            id="OrderPanel.additionalDaysSavingSaving"
            values={{ formattedSaving: savingsHint.formattedSaving }}
          />
        </span>
      </div>
    );
  }, [savingsHint]);

  // Customer line items and payin total
  const customerLineItems = useMemo(
    () => lineItems?.filter(item => item.includeFor.includes('customer')),
    [lineItems]
  );
  const payinTotal = useMemo(
    () =>
      customerLineItems && customerLineItems.length > 0
        ? estimatedTotalPrice(customerLineItems, marketplaceCurrency)
        : null,
    [customerLineItems, marketplaceCurrency]
  );
  const convertedPayinTotal = useMemo(
    () =>
      payinTotal
        ? convertPrice(
            exchangeRates,
            payinTotal.amount,
            payinTotal.currency,
            currentUserCurrency
          )
        : null,
    [payinTotal, exchangeRates, currentUserCurrency]
  );

  const { listingType, unitType, transactionProcessAlias = '' } =
    publicData || {};
  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  // Use defaultPriceKey fallback during initial render so SSR doesn't show false "no price".
  const effectivePackageKey = selectedPackage || defaultPriceKey;
  const hasAnyPackagePrice =
    !!pricePackages && Object.values(pricePackages).some(p => !!p);
  const currentPrice = customPackage
    ? customPackage.price
    : pricePackages
    ? pricePackages[effectivePackageKey]
    : null;
  const price = !!currentPrice
    ? new Money(currentPrice.amount, currentPrice.currency)
    : null;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const exchangeRatesReady = !fetchRatesInProgress && exchangeRates.length;

  // The mount-time fetch (loadData -> fetchMonthlyTimeSlots) only ever requests
  // "today + 1 month" - see the priority ladder below for why a draft (cart item/
  // rental defaults/storefront) booked further out than that needs its own fetch
  // before it can ever be validated. One onFetchTimeSlots call per missing month,
  // not one call spanning the whole range - fetchTimeSlots keys everything it
  // receives by monthIdString(start, ...), so a single multi-month call would file
  // every returned slot under just the first month's id and the later months would
  // never stop looking unfetched.
  const fetchTimeSlotsForDraft = draft => {
    getMissingMonthRangesForDraft(draft, monthlyTimeSlots, listingTimeZone).forEach(
      ({ start, end }) => {
        onFetchTimeSlots(listingId, start, end, listingTimeZone);
      }
    );
  };

  // RAS-2520: a cart item records the tier it was actually booked with, so when one
  // prefills this panel the toggle must move to that item's category - otherwise the
  // panel silently shows, say, an hourly cart entry underneath a "Daily" pill (the
  // category seeded from defaultPriceKey on mount), and the two disagree about which
  // form and which price the renter is looking at. Only cart drafts carry a package;
  // storefront ranges and remembered rental defaults don't, and deliberately leave the
  // category where it is.
  //
  // It also restores the item's own package, not just its category. handleDateRangeChange
  // below re-derives the package from the booking's duration, and duration cannot
  // distinguish a 1-night overnight booking from a 1-day daily one - they store the exact
  // same dates - so an overnight cart item came back re-derived as perDayPrice. That one
  // wrong value then cascaded: the price pill read "rental . 1 day" at the day rate, and
  // BookingDatesForm's own isDaily check (which deliberately excludes overnightPrice, so
  // the picker doesn't shift an overnight end date) flipped to true and redisplayed a
  // Jul 30 -> Jul 31 booking as "Jul 30 - Jul 30".
  const syncCategoryFromCartItem = cartItem => {
    const category = getDefaultCategory(cartItem?.package);
    if (category) {
      setSelectedCategory(category);
      setSelectedPackage(cartItem.package);
    }
  };

  // Consolidated: Handle both cart prefill and rental defaults with availability validation
  // Precedence: 1) Cart prefill (from URL) 2) Rental defaults 3) Fallback
  useEffect(() => {
    // Wait for required data
    if (
      !exchangeRatesReady ||
      allTimeSlotsReady === false ||
      !listingTimeZone ||
      !listingPublicData
    ) {
      return;
    }

    // Reset rental defaults processing if listing changed
    if (processedRentalDefaultsRef.current.listingId !== listingId) {
      processedRentalDefaultsRef.current = { listingId, processed: false };
    }

    // Priority 1: Cart prefill (from URL)
    if (
      cartItemIdFromUrl &&
      processedCartItemIdRef.current !== cartItemIdFromUrl
    ) {
      // Regression fix (manual QA): wait for the cart itself to finish loading before
      // looking up cartItemIdFromUrl. fetchCartItems (cart.duck.js) is a genuinely
      // async thunk - an sdk.currentUser.show() round-trip plus a denormalizing fetch
      // for authenticated users - with no guaranteed ordering relative to
      // exchangeRatesReady/allTimeSlotsReady above (unrelated data sources: currency
      // rates and availability time slots vs. the shopping cart). Priority 1.5 below
      // already guards on this exact cartState.items !== null signal for the same
      // reason; Priority 1 didn't, so whenever cart data resolved slower than the
      // listing's own readiness checks, getCartItemById returned null purely because
      // cartState.items was still null - not because the item didn't exist - which
      // fell into the "not found" branch below: permanently marking this cartItemId
      // as checked and clearing it from the URL before the cart ever got a real
      // chance to answer, discarding a genuinely valid cart-item prefill with no
      // retry (this is exactly why the calendar opened blank on a fresh cart->listing
      // navigation - the URL still visibly lost its cartItemId, since that clearing
      // happened on this same "not found" path, but no prefill state was ever set).
      // Returning here - without touching processedCartItemIdRef or the URL - lets
      // this block naturally re-run once cartState updates (already a dependency of
      // this effect) and cart items have actually loaded, instead of giving up.
      if (cartState.items === null) {
        return;
      }

      const cartItem = getCartItemById(cartState, cartItemIdFromUrl);

      // Regression fix (manual QA): computeInitialValuesFromCartItem's date-based
      // validation (firstBlockedBetween -> pickMonthlyTimeSlots) can't distinguish
      // "this month hasn't been fetched into monthlyTimeSlots yet" from "it's fetched,
      // and genuinely no slot covers this day" - both read back as an empty array -
      // so it silently returns "invalid" instead of "not ready yet". The mount-time
      // fetch only ever requests "today + 1 month" (see fetchMonthData's callers), so
      // any cart item booked further out than that always failed this exact way on
      // its first (and, since processedCartItemIdRef is marked below regardless of
      // outcome, only) attempt - the calendar opened blank with no retry, even once
      // the real month's data arrived moments later. Bailing out here - before
      // processedCartItemIdRef gets marked - lets this re-run once monthlyTimeSlots
      // updates (already a dependency of this effect), same as the cartState.items
      // guard above.
      if (
        cartItem &&
        !hasRequiredMonthlyTimeSlotsForDraft(cartItem, monthlyTimeSlots, listingTimeZone)
      ) {
        fetchTimeSlotsForDraft(cartItem);
        return;
      }

      // Mark this cartItemId as examined regardless of what happens below - the two
      // failure paths inside this block (wrong listing at the check just below, or
      // computeInitialValuesFromCartItem returning null e.g. because the item's dates
      // are no longer available) used to fall through WITHOUT marking anything, which
      // left processedCartItemIdRef permanently unset for this id. Since Priority 3
      // below is itself gated on `processedCartItemIdRef.current === cartItemIdFromUrl`,
      // that meant Priority 3 could never run either - the whole effect would return
      // having decided nothing, for every future re-render, for as long as the stale
      // cartItemId stayed in the URL. Downstream, prefillCheckComplete (which mirrors
      // processedRentalDefaultsRef.current.processed) would never become true, leaving
      // the booking calendar's "still checking" dimmed state stuck forever. The
      // not-found branch below already did this correctly; this just makes it apply to
      // every path through this block, not only that one.
      processedCartItemIdRef.current = cartItemIdFromUrl;
      if (cartItem) {
        // Validate cart item belongs to this listing
        const cartListingId =
          cartItem.listingId ||
          cartItem.listing?.id?.uuid ||
          cartItem.listing?.id;
        if (listingId === cartListingId) {
          const initialValuesFromCartItem = computeInitialValuesFromCartItem(
            cartItem,
            listing,
            monthlyTimeSlots,
            listingTimeZone
          );

          const { initialValues, isDailyPackage } =
            initialValuesFromCartItem || {};

          if (initialValues) {
            setValidatedInitialValues(initialValues);
            syncCategoryFromCartItem(cartItem);

            if (isDailyPackage) {
              handleDateRangeChange(
                initialValues.bookingDates,
                pricePackages,
                setSelectedPackage,
                setCustomPackage,
                // The cart item's own package, so an overnight booking keeps its night
                // rate instead of being re-derived from duration as a day booking.
                cartItem.package
              );
              setDatesSelected(
                !!(
                  initialValues.bookingDates?.startDate &&
                  initialValues.bookingDates?.endDate
                )
              );
            } else {
              handleDateTimeRangeChange(
                initialValues,
                pricePackages,
                setCustomPackage,
                setSelectedPackage
              );
              setDatesSelected(
                !!(
                  initialValues.bookingStartTime && initialValues.bookingEndTime
                )
              );
            }

            processedCartItemIdRef.current = cartItemIdFromUrl;
            processedRentalDefaultsRef.current = { listingId, processed: true }; // Skip rental defaults when cart prefill is used

            // Clear URL
            const updatedParams = { ...searchParams };
            delete updatedParams.cartItemId;
            const newSearchString = `?${stringify(updatedParams)}`;
            history.replace(
              `${location.pathname}${newSearchString}`,
              location.state
            );
            return; // Cart prefill takes precedence, skip rental defaults
          }
        }
      } else {
        // Cart item not found - mark as processed and clear URL
        processedCartItemIdRef.current = cartItemIdFromUrl;
        const updatedParams = { ...searchParams };
        delete updatedParams.cartItemId;
        const newSearchString = `?${stringify(updatedParams)}`;
        history.replace(
          `${location.pathname}${newSearchString}`,
          location.state
        );
      }
    }

    // Priority 2: URL date range prefill (storefront or cross-listing navigation).
    // Checked before the cart-for-listing fallback below - a date range the user just
    // carried in from search/store is a fresher, more deliberate signal than a cart
    // entry for this same listing added at some earlier, unrelated time. Only an exact
    // cartItemId in the URL (Priority 1 above - the user explicitly opened THIS cart
    // item) is allowed to take precedence over it.
    if (
      storefrontDateRange?.startDate &&
      storefrontDateRange?.endDate &&
      !processedRentalDefaultsRef.current.processed
    ) {
      const storefrontDraft = {
        bookingDates: storefrontDateRange,
        seats: 1,
      };

      // Same reasoning as Priority 1's identical guard above. Without this, an
      // unready monthlyTimeSlots makes computeInitialValuesFromDraft return null
      // below, which (since this block doesn't mark processedRentalDefaultsRef on
      // failure) falls straight through into the cart-for-listing fallback / Priority 3
      // in this same pass - and Priority 3's own "no rental defaults" branch marks
      // processed = true unconditionally, permanently stealing this listing's one shot
      // at ever retrying the storefront prefill once the real month's data arrives.
      if (!hasRequiredMonthlyTimeSlotsForDraft(storefrontDraft, monthlyTimeSlots, listingTimeZone)) {
        fetchTimeSlotsForDraft(storefrontDraft);
        return;
      }

      const initialValuesFromStorefront = computeInitialValuesFromDraft(
        storefrontDraft,
        listing,
        monthlyTimeSlots,
        listingTimeZone
      );

      const { initialValues, isDailyPackage } =
        initialValuesFromStorefront || {};

      if (initialValues) {
        setValidatedInitialValues(initialValues);

        if (isDailyPackage) {
          handleDateRangeChange(
            initialValues.bookingDates,
            pricePackages,
            setSelectedPackage,
            setCustomPackage
          );
          setDatesSelected(
            !!(
              initialValues.bookingDates?.startDate &&
              initialValues.bookingDates?.endDate
            )
          );
        } else {
          handleDateTimeRangeChange(
            initialValues,
            pricePackages,
            setCustomPackage,
            setSelectedPackage
          );
          setDatesSelected(
            !!(initialValues.bookingStartTime && initialValues.bookingEndTime)
          );
        }

        processedRentalDefaultsRef.current = { listingId, processed: true };
        return;
      }
    }

    // Priority 2.5: cart-for-listing fallback (no cartItemId in URL, and no fresher URL
    // date range applied above). Handles the "back from checkout" case: URL no longer
    // has cartItemId but cart still holds the item with selectedFeeTypes,
    // pickupAtOwnerLocation, etc. Uses listingId as the ref value so it auto-resets when
    // the user views a different listing.
    // Skip when cartState.items is null (still loading) so we retry once the cart is loaded.
    //
    // Manual QA (footer went blank after Rent Now): the ref used to hold just the
    // listingId, so this block ran exactly once per listing and then never again.
    // That made adding THIS listing to the cart invisible to the panel - and closing
    // the booking modal remounts the form (see modalEpoch), whose FormSpy then reports
    // an empty range and resets datesSelected, so the sticky footer fell back to
    // "Select Dates" with no price even though the item was sitting in the cart. Keying
    // the ref on the matched cart item's id as well as the listing means a newly added
    // (or swapped) cart item is a new key, so this same well-trodden prefill path
    // re-runs and repopulates dates/fees/fulfilment from the cart - which is exactly
    // the "prefilled date range state" the panel should be in at that point. It still
    // runs at most once per distinct cart item, so no loop.
    //
    // Manual QA (stale cart entry silently overriding a fresh search/store date range):
    // this block used to run BEFORE the URL date range check above, with no
    // !processedRentalDefaultsRef.current.processed gate of its own - so a listing
    // already sitting in the cart from some earlier, unrelated visit always won over
    // whatever date range the user had just carried in from search/store, even though
    // they'd clearly just expressed a fresh intent by picking those dates and clicking
    // through. Moving this block after the URL date range check, and adding the same
    // processed gate every other priority here already has, means a fresh URL date
    // range wins; this is now reached only as a fallback when there isn't one.
    const cartItemForListing =
      !cartItemIdFromUrl && cartState.items !== null
        ? getCartItemByListingId(cartState, listingId)
        : null;
    const cartItemByListingKey = `${listingId}:${cartItemForListing?.id || 'none'}`;

    if (
      !cartItemIdFromUrl &&
      cartState.items !== null &&
      processedCartItemByListingRef.current !== cartItemByListingKey &&
      !processedRentalDefaultsRef.current.processed
    ) {
      // Same reasoning as Priority 1's identical guard above: don't mark this
      // listingId checked (skipping it forever) until monthlyTimeSlots actually
      // covers the months this item's dates touch.
      if (
        cartItemForListing &&
        !hasRequiredMonthlyTimeSlotsForDraft(cartItemForListing, monthlyTimeSlots, listingTimeZone)
      ) {
        fetchTimeSlotsForDraft(cartItemForListing);
        return;
      }

      processedCartItemByListingRef.current = cartItemByListingKey; // cart loaded — mark checked

      if (cartItemForListing) {
        const initialValuesFromCartItem = computeInitialValuesFromCartItem(
          cartItemForListing,
          listing,
          monthlyTimeSlots,
          listingTimeZone
        );

        const { initialValues, isDailyPackage } = initialValuesFromCartItem || {};

        if (initialValues) {
          setValidatedInitialValues(initialValues);
          syncCategoryFromCartItem(cartItemForListing);

          if (isDailyPackage) {
            handleDateRangeChange(
              initialValues.bookingDates,
              pricePackages,
              setSelectedPackage,
              setCustomPackage,
              cartItemForListing.package
            );
            setDatesSelected(
              !!(initialValues.bookingDates?.startDate && initialValues.bookingDates?.endDate)
            );
          } else {
            handleDateTimeRangeChange(
              initialValues,
              pricePackages,
              setCustomPackage,
              setSelectedPackage
            );
            setDatesSelected(!!(initialValues.bookingStartTime && initialValues.bookingEndTime));
          }

          processedRentalDefaultsRef.current = { listingId, processed: true };
          return;
        }
      }
    }

    // Priority 3: Rental defaults (only if no cart prefill and not already processed for this listing)
    if (
      (!cartItemIdFromUrl ||
        processedCartItemIdRef.current === cartItemIdFromUrl) &&
      !processedRentalDefaultsRef.current.processed
    ) {
      if (!rentalDefaults) {
        processedRentalDefaultsRef.current = { listingId, processed: true };
        return;
      }

      // Only validate if we have dates/times
      const hasDates =
        rentalDefaults.bookingDates?.startDate &&
        rentalDefaults.bookingDates?.endDate;
      const hasTimes =
        rentalDefaults.bookingStartTime && rentalDefaults.bookingEndTime;

      if (!hasDates && !hasTimes) {
        processedRentalDefaultsRef.current = { listingId, processed: true };
        return;
      }

      // Convert rental defaults to draft format.
      // Do not rehydrate optional self pickup/return selections as defaults.
      // Backend will apply fallback self options if user proceeds without selecting.
      const sanitizedRentalDefaults = {
        ...rentalDefaults,
        pickupAtOwnerLocation:
          deliveryPickup?.isDeliveryRequired
            ? rentalDefaults.pickupAtOwnerLocation
            : null,
        returnAtOwnerLocation:
          deliveryPickup?.isPickupRequired
            ? rentalDefaults.returnAtOwnerLocation
            : null,
      };

      const draft = {
        ...sanitizedRentalDefaults,
        bookingDates: hasDates
          ? {
              startDate: new Date(rentalDefaults.bookingDates.startDate),
              endDate: new Date(rentalDefaults.bookingDates.endDate),
            }
          : null,
      };

      // Same reasoning as Priority 1's identical guard above - skip (without marking
      // processed) rather than let an unready monthlyTimeSlots read back as a
      // permanent "invalid rental defaults" rejection.
      if (!hasRequiredMonthlyTimeSlotsForDraft(draft, monthlyTimeSlots, listingTimeZone)) {
        fetchTimeSlotsForDraft(draft);
        return;
      }

      // Validate (expensive operation, but only runs when needed)
      const initialValuesFromDraft = computeInitialValuesFromDraft(
        draft,
        listing,
        monthlyTimeSlots,
        listingTimeZone
      );

      const { initialValues, isDailyPackage } = initialValuesFromDraft || {};

      if (initialValues) {
        setValidatedInitialValues(initialValues);

        if (isDailyPackage) {
          handleDateRangeChange(
            initialValues.bookingDates,
            pricePackages,
            setSelectedPackage,
            setCustomPackage
          );
          setDatesSelected(
            !!(
              initialValues.bookingDates?.startDate &&
              initialValues.bookingDates?.endDate
            )
          );
        } else {
          handleDateTimeRangeChange(
            initialValues,
            pricePackages,
            setCustomPackage,
            setSelectedPackage
          );
          setDatesSelected(
            !!(initialValues.bookingStartTime && initialValues.bookingEndTime)
          );
        }
      }

      processedRentalDefaultsRef.current = { listingId, processed: true };

      return;
    }
  }, [
    cartItemIdFromUrl,
    exchangeRatesReady,
    allTimeSlotsReady,
    isStorefrontFlow,
    listingId,
    listingTimeZone,
    listingPublicData,
    processedCartItemIdRef,
    cartState,
    pricePackages,
    searchParams,
    rentalDefaults,
    storefrontDateRange,
  ]);

  // Reset the "checked this listing" gate whenever the listing itself changes, so a
  // stale `true` from the PREVIOUS listing can't let the calendar open prematurely for
  // the new one before its own check has actually run.
  useEffect(() => {
    setPrefillCheckComplete(false);
  }, [listingId]);

  // RAS-2520: selectedCategory's useState initialiser only runs on mount, so a listing
  // swap without a remount (same OrderPanel instance, different listing) would otherwise
  // carry the previous listing's toggle choice over to one that may not even sell that
  // category. Keyed on listingId as well as defaultPriceKey because two different
  // listings can share a defaultPriceKey - in that case defaultPriceKey alone never
  // changes and a stale "Hourly" would silently persist. Setting the same value is a
  // no-op re-render-wise, so this costs nothing on the normal single-listing path.
  useEffect(() => {
    setSelectedCategory(getDefaultCategory(defaultPriceKey) || availableCategories[0] || null);
    // availableCategories is rebuilt every render, so its first entry (a plain string) is
    // the dependency, not the array itself - depending on the array would re-run this on
    // every single render and stomp the user's toggle choice on the very next one.
  }, [listingId, defaultPriceKey, availableCategories[0]]);

  // Deliberately separate from the priority effect above rather than folded into it -
  // that effect has several early-return branches (one per priority) and duplicating
  // "mark complete" before each one is easy to get subtly wrong. processedRentalDefaultsRef
  // is set to {listingId, processed:true} at the end of every one of those branches
  // already (it's the priority effect's own internal "done with this listing" signal),
  // so this just mirrors that ref into state that BookingDatesForm/BookingTimeForm can
  // actually react to (refs don't trigger re-renders on their own). Runs immediately
  // after the priority effect on every render pass that shares its dependencies, so it
  // always observes a value the priority effect just finished setting.
  useEffect(() => {
    if (
      processedRentalDefaultsRef.current.listingId === listingId &&
      processedRentalDefaultsRef.current.processed
    ) {
      setPrefillCheckComplete(true);
    }
  }, [
    cartItemIdFromUrl,
    exchangeRatesReady,
    allTimeSlotsReady,
    isStorefrontFlow,
    listingId,
    listingTimeZone,
    listingPublicData,
    cartState,
    pricePackages,
    searchParams,
    rentalDefaults,
    storefrontDateRange,
  ]);

  // When pricePackages changes (e.g. currentUserCurrency finalises after user profile loads,
  // or exchange rates arrive after the prefill effect already ran and marked itself processed),
  // recompute customPackage.price so the custom chip always reflects the current currency.
  useEffect(() => {
    if (!customPackage || !pricePackages) return;

    let updatedPrice = null;

    if (customPackage.days) {
      updatedPrice = getCustomPackagePrice(customPackage.days, pricePackages);
    } else if (customPackage.nights != null && pricePackages[OVERNIGHT_PRICE]) {
      // RAS-2520: same N x rate recompute the hour/minute branches do - deliberately NOT
      // getCustomPackagePrice, whose packageDurations map has no overnightPrice entry
      // (it prorates day tiers, which is a different question from a per-night rate).
      const amount = convertMoneyToDecimalJS(pricePackages[OVERNIGHT_PRICE]);
      const totalAmount = decimalJSToNumber(amount.mul(customPackage.nights));
      updatedPrice = new Money(totalAmount, pricePackages[OVERNIGHT_PRICE].currency);
    } else if (customPackage.hours != null && pricePackages[PER_HOUR]) {
      const amount = convertMoneyToDecimalJS(pricePackages[PER_HOUR]);
      const totalAmount = decimalJSToNumber(amount.mul(customPackage.hours));
      updatedPrice = new Money(totalAmount, pricePackages[PER_HOUR].currency);
    } else if (customPackage.minutes != null && pricePackages[PER_MINUTE]) {
      const amount = convertMoneyToDecimalJS(pricePackages[PER_MINUTE]);
      const totalAmount = decimalJSToNumber(amount.mul(customPackage.minutes));
      updatedPrice = new Money(totalAmount, pricePackages[PER_MINUTE].currency);
    }

    if (updatedPrice) {
      setCustomPackage(prev => {
        if (
          prev?.price?.currency === updatedPrice.currency &&
          prev?.price?.amount === updatedPrice.amount
        ) {
          return prev;
        }
        return { ...prev, price: updatedPrice };
      });
    }
  }, [pricePackages]);  

  // ========== EARLY RETURN - after all hooks ==========
  if (!defaultPriceKey) {
    return (
      <div className={css.error}>
        <FormattedMessage
          id="OrderPanel.defaultPriceKeyMissing"
          defaultMessage="Default price key is not set"
        />
      </div>
    );
  }

  const packageAvgPrice = getPackageAvgPrice(
    price,
    selectedPackage,
    customPackage
  );

  const convertedAveragePrice = getConvertedPrice({
    packageAvgPrice,
    exchangeRatesReady,
    exchangeRates,
    currentUserCurrency,
  });

  const showPriceMissing =
    isPaymentProcess &&
    exchangeRatesReady &&
    !price &&
    !hasAnyPackagePrice;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };

  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const showFetchRatesError =
    fetRatesError && !exchangeRates?.length && !fetchRatesInProgress;

  const FetchExchangeRatesError = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.fetchExchangeRatesError" />
      </p>
    );
  };

  // We need to add dynamic dynamic fields to form for "Usage multiple calc type charges"
  const { additionalFees = [] } = publicData || {};

  // Convert the additionalFees rate to the current currency's equivalent rate
  const convertedAdditionalFees = useMemo(
    () =>
      additionalFees.map(fee => {
        const { rate } = fee;

        // Since rate is in big unit, but the money object expect in small
        // we need to convert it into small units.
        const divisor = unitDivisor(basePrice.currency);
        const updatedRate = rate * divisor;
        if (rate && exchangeRates?.length) {
          return {
            ...fee,
            rate: convertPrice(
              exchangeRates,
              updatedRate,
              basePrice.currency,
              currentUserCurrency
            ),
          };
        }
        return fee;
      }),
    [additionalFees, exchangeRates, currentUserCurrency]
  );

  const stringifiedAdditionalFees =
    additionalFees && additionalFees.length > 0
      ? JSON.stringify(additionalFees)
      : null;

  // Organize all fees into clear categories: deliveryPickup and addons
  const organizedFees = useMemo(
    () =>
      organizeFees(
        convertedAdditionalFees,
        DELIVERY_FEE,
        PICKUP_FEE,
        USAGE_MULTIPLE,
        REQUIRED_UPFRONT_OPTIONAL,
        REQUIRED_UPFRONT
      ),
    [convertedAdditionalFees]
  );

  const { deliveryPickup, addons } = organizedFees;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  // const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType) && customPackage === null;

  // Manual QA: this was plain `selectedPackage || defaultPriceKey`, and selectedPackage
  // can be seeded from the user's remembered cross-listing preference (rental defaults
  // in localStorage, replayed through handleDateRangeChange/handleDateTimeRangeChange).
  // Since currentPackage decides which booking form renders - and, via footerPriceProps,
  // what the sticky footer shows - a remembered hourly/minute preference made a
  // day-based listing open the time picker (and vice versa), i.e. a form the listing
  // doesn't actually sell. The listing's own defaultPriceKey is the authority on the
  // modality; a remembered preference may only pick a tier WITHIN that modality (e.g.
  // 2-day vs weekly on a day-based listing), never switch it. Falling back to
  // defaultPriceKey on a modality mismatch keeps the remembered-preference feature
  // working where it's valid and drops it exactly where the listing can't honour it.
  //
  // RAS-2520: with the category toggle, the ACTIVE CATEGORY - not defaultPriceKey - is
  // that authority, since the whole point of the toggle is to book a listing in a
  // category other than its configured default. On a single-category listing the two are
  // by construction the same value (selectedCategory is seeded from defaultPriceKey and
  // there is no toggle to change it), and selectedPackage can only ever hold a key the
  // listing actually sells, so this resolves identically to the pre-toggle logic there.
  // Comparing full categories rather than a time-based/day-based boolean also lets
  // Hourly and Minute clamp against each other, which the old two-way split could not.
  const isTimeBasedPackage = pkg => [PER_HOUR, PER_MINUTE].includes(pkg);
  const effectiveCategory = selectedCategory || getDefaultCategory(defaultPriceKey);
  // Used below and by the mobile footer. Falls back to defaultPriceKey's own modality
  // when the listing's price key isn't in any known category, preserving old behaviour.
  const categoryIsTimeBased = effectiveCategory
    ? [BOOKING_CATEGORY_HOURLY, BOOKING_CATEGORY_MINUTE].includes(effectiveCategory)
    : isTimeBasedPackage(defaultPriceKey);
  // The tier to fall back to when selectedPackage belongs to another category - the
  // listing's own default when that default is in the active category, otherwise the
  // shortest tier the listing sells in it (getBookingCategoryPriceKeys is tier-ordered).
  const categoryFallbackPackage = pickPackageForCategory(
    prices,
    effectiveCategory,
    defaultPriceKey
  );
  const selectedPackageMatchesCategory =
    selectedPackage && getDefaultCategory(selectedPackage) === effectiveCategory;
  const currentPackage = selectedPackageMatchesCategory
    ? selectedPackage
    : categoryFallbackPackage || defaultPriceKey || selectedPackage;

  const shouldHaveBookingTime =
    isBooking &&
    currentPackage &&
    [PER_HOUR, PER_MINUTE].includes(currentPackage) &&
    (customPackage === null || customPackage?.hours || customPackage?.minutes);
  const showBookingTimeForm =
    shouldHaveBookingTime && !isClosed && timeZone && exchangeRatesReady;

  // const shouldHaveBookingDates =
  //   isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const shouldHaveBookingDates =
    isBooking &&
    currentPackage &&
    (DAY_PACKAGES.includes(currentPackage) ||
      (customPackage !== null && customPackage.days));
  const showBookingDatesForm =
    shouldHaveBookingDates && !isClosed && timeZone && exchangeRatesReady;

  // Simple precedence check - no useMemo needed (cheap operation)
  // validatedInitialValues is already computed in useEffect with proper precedence
  const effectiveInitialValues = validatedInitialValues || { seats: 1 };
  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock =
    isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    isPurchase && typeof currentStock === 'number' && exchangeRatesReady;

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo
    .map(info => info.name)
    .includes(processName);

  const { pickupEnabled, shippingEnabled } =
    listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [
    STOCK_MULTIPLE_ITEMS,
    STOCK_INFINITE_MULTIPLE_ITEMS,
  ].includes(listingTypeConfig?.stockType);

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const handleSubmitWithPackage = useCallback(
    e => {
      // CRITICAL FIX: Ensure we submit the correct package
      // If customPackage exists, selectedPackage should already be set correctly by handleDateRangeChange/handleDateTimeRangeChange
      // But double-check to prevent any edge cases
      let packageToSubmit = selectedPackage;

      // If customPackage has days, ensure we're using a day-based package
      if (customPackage?.days && !DAY_PACKAGES.includes(selectedPackage)) {
        // Fallback to PER_DAY if selectedPackage is hour/minute but customPackage is day-based
        packageToSubmit = PER_DAY;
      }

      // If customPackage has hours/minutes, ensure we're using hour/minute package
      if (
        (customPackage?.hours || customPackage?.minutes) &&
        ![PER_HOUR, PER_MINUTE].includes(selectedPackage)
      ) {
        // Keep selectedPackage as is (should already be correct)
        packageToSubmit = selectedPackage;
      }

      onSubmit({ ...e, package: packageToSubmit });
    },
    [onSubmit, selectedPackage, customPackage]
  );

  const handleDateRangeChangeCallback = useCallback(
    range => {
      handleDateRangeChange(
        range,
        pricePackages,
        setSelectedPackage,
        setCustomPackage,
        selectedPackage
      );
      setDatesSelected(!!(range?.startDate && range?.endDate));
      const hint =
        range?.startDate && range?.endDate
          ? getSavingsHint(range, pricePackages, intl)
          : null;
      setSavingsHint(hint);
    },
    [pricePackages, intl, selectedPackage]
  );

  const handleTimeRangeChangeCallback = useCallback(
    range => {
      handleDateTimeRangeChange(
        range,
        pricePackages,
        setCustomPackage,
        setSelectedPackage
      );
      setDatesSelected(!!(range?.bookingStartTime && range?.bookingEndTime));
    },
    [pricePackages]
  );

  const removeCustomPackageCallback = useCallback(() => {
    setCustomPackage(null);
    setSavingsHint(null);
  }, []);

  const { profile, createdAt } = author?.attributes || {};
  const { publicData: uPublicData, displayName } = profile || {};
  const { city, rating, storeAddress } = uPublicData || {};
  const storeCitySlug = generateCitySlug(storeAddress?.city || city);
  const searchString =
    !!city && !!displayName
      ? `?name=${displayName}&city=${city}`
      : !!displayName
      ? `?name=${displayName}`
      : !!city
      ? `?city=${city}`
      : '';

  const linkProps = author.id
    ? {
        name: 'ProfilePage',
        params: {
          ...(storeCitySlug ? { city: storeCitySlug } : {}),
          slug: generateStoreSlug(uPublicData?.storeName),
          id: author.id.uuid
        },
        to: { search: '' },
      }
    : { name: 'ProfileBasePage' };

  const ratingPercent = '80%';
  const messageOwnerLabel = intl.formatMessage({
    id: 'AboutOwner.messageOwner',
  });

  // RAS-2520: switching category discards any in-progress selection, because a date
  // range and a time range aren't translatable into one another - carrying a 3-day
  // selection into the Hourly picker would either silently mis-price or resolve to a
  // package the new category doesn't contain. Everything cleared here is this
  // component's own selection state; nothing touches the prefill ladder's refs, which
  // have already marked this listing processed and so won't re-run and re-prefill.
  //
  // The remount is what actually resets the pickers: BookingDatesForm/BookingTimeForm
  // both seed their "State A vs State C" shape and their focused/current month from
  // construction-time values (see each form's constructor/useState seeds), so they only
  // ever return to an empty initial state by being rebuilt. Bumping modalEpoch - the
  // existing remount signal, already keying the mobile modal's content - and using it as
  // a key on the forms themselves covers desktop too, where Hourly -> Minute would
  // otherwise keep the very same BookingTimeForm instance mounted with its old times.
  const handleBookingCategoryChange = newCategory => {
    if (!newCategory || newCategory === effectiveCategory) {
      return;
    }

    setSelectedCategory(newCategory);
    setSelectedPackage(pickPackageForCategory(prices, newCategory, defaultPriceKey));
    setCustomPackage(null);
    // Without this the remounted form is handed the OLD category's validated dates
    // straight back through effectiveInitialValues and re-prefills itself with them.
    setValidatedInitialValues(null);
    setBookingDates(null);
    setDatesSelected(false);
    setSavingsHint(null);
    setModalEpoch(e => e + 1);
  };

  //
  // RAS-2520: clamped against the ACTIVE CATEGORY rather than defaultPriceKey, for the
  // same reason currentPackage is above - after switching a day-defaulted listing to
  // Hourly, a leftover { days: 3 } customPackage must not render "$102.40 for 3 days"
  // under an hourly rate. categoryIsTimeBased resolves to isTimeBasedPackage(
  // defaultPriceKey) whenever there's no known category, so single-tier listings behave
  // exactly as before.
  // RAS-2520: a { nights } customPackage belongs to the Overnight category and nowhere
  // else - showing it under Daily would price a day booking at the night rate. Every
  // other shape keeps the pre-existing day-vs-time check exactly as it was.
  const customPackageMatchesListingModality =
    customPackage &&
    (customPackage.nights
      ? effectiveCategory === BOOKING_CATEGORY_OVERNIGHT
      : categoryIsTimeBased === !!(customPackage.hours || customPackage.minutes));
  const displayCustomPackage = customPackageMatchesListingModality
    ? customPackage
    : null;

  const panelContent = (
    <>
      <div className={classes}>
        <div className={css.topBorder}>
          <div className={css.topBorderContent} id="topBorderContent">
            <div className={css.modalHeading}>
              <H2 className={css.heading}>{title}</H2>
            </div>
            <div className={css.orderHeading}>
              {titleDesktop ? (
                titleDesktop
              ) : (
                <H2 className={titleClasses}>{title}</H2>
              )}
              {subTitleText ? (
                <div className={css.orderHelp}>{subTitleText}</div>
              ) : null}
            </div>

            {/* RAS-2520: between the "Book This Rental" heading and the price pill, on
                both desktop and mobile (panelContent is shared by both paths). Renders
                nothing at all on single-category listings - see the component. */}
            <BookingCategoryToggle
              categories={availableCategories}
              selectedCategory={effectiveCategory}
              onChange={handleBookingCategoryChange}
            />

            <div className={css.priceAndPackages} id="priceAndPackages">
              <PriceMaybe
                price={convertedAveragePrice}
                publicData={publicData}
                validListingTypes={validListingTypes}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                selectedCurrency={currentUserCurrency}
                selectedPackage={currentPackage}
                customPackage={displayCustomPackage}
                pricePackages={pricePackages}
                datesSelected={datesSelected}
                exchangeRatesReady={exchangeRatesReady}
                exchangeRates={exchangeRates}
                bookingCategory={effectiveCategory}
                isMultiCategory={availableCategories.length > 1}
              />
            </div>
          </div>

          {/* {bookingDates ? (
              <PromotionalTextMaybe
                bookingDates={bookingDates}
                selectedPackage={selectedPackage}
                packages={pricePackages}
                currentUserCurrency={currentUserCurrency}
                intl={intl}
              />
            ) : null} */}

          {showPriceMissing ? (
            <PriceMissing />
          ) : showFetchRatesError ? (
            <FetchExchangeRatesError />
          ) : showBookingTimeForm ? (
            <BookingTimeForm
              // RAS-2520: remount on category switch so the picker returns to its empty
              // initial state (see handleBookingCategoryChange). modalEpoch is otherwise
              // only ever bumped when the MOBILE booking modal closes, and that path
              // already remounts this subtree via the wrapper's own key - so on desktop
              // this key changes only on a category switch, and on mobile it changes
              // exactly when the subtree was being rebuilt anyway.
              key={modalEpoch}
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              initialValues={effectiveInitialValues}
              prefillCheckComplete={prefillCheckComplete}
              lineItemUnitType={lineItemUnitType}
              onSubmit={handleSubmitWithPackage}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              listing={listing}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(
                TODAY,
                dateFormattingOptions
              )}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              selectedPackage={currentPackage}
              showCartButton={showCartButton}
              currentUser={currentUser}
              isCurrentUserListing={isCurrentUserListing}
              onAddToCart={onAddToCart}
              addToCartInProgress={addToCartInProgress}
              addToCartError={addToCartError}
              cartNotification={cartNotification}
              disableSubmit={isStorefrontFlow}
              authorId={author?.id?.uuid}
              config={config}
              history={history}
              routeConfiguration={routeConfiguration}
              stringifiedAdditionalFees={stringifiedAdditionalFees}
              currentUserCurrency={currentUserCurrency}
              deliveryPickup={deliveryPickup}
              addons={addons}
              initialScroll={!isDesktopLayout}
              maxAvailableSeats={maxAvailableSeats}
              isPickupAtOwnerLocation={isPickupAtOwnerLocation}
              breakdownTitle={breakdownTitle}
              isShowCommissionLineItem={isShowCommissionLineItem}
              payinTotal={convertedPayinTotal}
              onTimeRangeChange={handleTimeRangeChangeCallback}
              removeCustomPackage={removeCustomPackageCallback}
              refundMessage={refundMessage}
              verificationMessage={verificationMessage}
              autoFocused={autoFocused}
              setAutoFocused={setAutoFocused}
              customPackage={customPackage}
              showAuthorDetails={showAuthorDetails}
              author={author}
              authorLink={authorLink}
              isVerified={isVerified}
              reviews={reviews}
              totalListings={totalListings}
              onContactUser={onContactUser}
              ratingPercent={ratingPercent}
              rating={rating}
              linkProps={linkProps}
              messageOwnerLabel={messageOwnerLabel}
            />
          ) : showBookingDatesForm ? (
            <BookingDatesForm
              // RAS-2520: same remount-on-category-switch reasoning as BookingTimeForm
              // above.
              key={modalEpoch}
              usersEmailVerified={usersEmailVerified}
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              lineItemUnitType={lineItemUnitType}
              setBookingDates={setBookingDates}
              onSubmit={handleSubmitWithPackage}
              disableSubmit={isStorefrontFlow}
              price={price}
              initialValues={effectiveInitialValues}
              prefillCheckComplete={prefillCheckComplete}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              listing={listing}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              selectedPackage={currentPackage}
              customPackage={customPackage}
              showCartButton={showCartButton}
              currentUser={currentUser}
              isCurrentUserListing={isCurrentUserListing}
              onAddToCart={onAddToCart}
              addToCartInProgress={addToCartInProgress}
              addToCartError={addToCartError}
              authorId={author?.id?.uuid}
              config={config}
              cartNotification={cartNotification}
              history={history}
              routeConfiguration={routeConfiguration}
              autoFocused={autoFocused}
              setAutoFocused={setAutoFocused}
              stringifiedAdditionalFees={stringifiedAdditionalFees}
              currentUserCurrency={currentUserCurrency}
              deliveryPickup={deliveryPickup}
              addons={addons}
              initialScroll={!isDesktopLayout}
              maxAvailableSeats={maxAvailableSeats}
              isPickupAtOwnerLocation={isPickupAtOwnerLocation}
              breakdownTitle={breakdownTitle}
              isShowCommissionLineItem={isShowCommissionLineItem}
              payinTotal={convertedPayinTotal}
              onDateRangeChange={handleDateRangeChangeCallback}
              removeCustomPackage={removeCustomPackageCallback}
              savingPerDayMessage={savingPerDayMessage}
              refundMessage={refundMessage}
              verificationMessage={verificationMessage}
              showAuthorDetails={showAuthorDetails}
              author={author}
              authorLink={authorLink}
              isVerified={isVerified}
              reviews={reviews}
              totalListings={totalListings}
              onContactUser={onContactUser}
              ratingPercent={ratingPercent}
              rating={rating}
              linkProps={linkProps}
              messageOwnerLabel={messageOwnerLabel}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              onSubmit={onSubmit}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              currentStock={currentStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onContactUser={onContactUser}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
            />
          ) : showInquiryForm ? (
            <InquiryWithoutPaymentForm
              formId="OrderPanelInquiryForm"
              onSubmit={onSubmit}
            />
          ) : !isKnownProcess ? (
            <p className={css.errorSidebar}>
              <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
            </p>
          ) : null}
        </div>

        <div className={css.openOrderFormContainer}>
          <div
            className={classNames(
              css.openOrderForm,
              !scrolling ? css.openOrderFormMargin : null
            )}
          >
            <PrimaryButton
              onClick={() =>
                typeof window !== 'undefined'
                  ? (window.location.href = `tel:${locationBasedPhoneNumber}`)
                  : null
              }
              className={css.callButton}
            >
              <FormattedMessage id="OrderPanel.callButtonText" />
            </PrimaryButton>

            {(() => {
              const ctaDisabledReason = isClosed
                ? <FormattedMessage id="OrderPanel.closedListingTooltip" />
                : isOutOfStock
                ? <FormattedMessage id="OrderPanel.outOfStockTooltip" />
                : null;

              const buttonLabel = isClosed || isOutOfStock
                ? isClosed
                  ? <FormattedMessage id="OrderPanel.closedListingButtonText" />
                  : <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
                : isBooking
                ? <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
                : isPurchase
                ? <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
                : <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />;

              if (ctaDisabledReason) {
                return (
                  <DisabledCtaWithTooltip
                    tooltipContent={ctaDisabledReason}
                    className={css.disabledCtaWrapper}
                  >
                    <PrimaryButton disabled className={css.reserveButton}>
                      {buttonLabel}
                    </PrimaryButton>
                  </DisabledCtaWithTooltip>
                );
              }

              return (
                <PrimaryButton
                  onClick={handleSubmit(
                    isOwnListing,
                    isClosed,
                    showInquiryForm,
                    onSubmit,
                    history,
                    location,
                    selectedPackage
                  )}
                  className={css.reserveButton}
                >
                  {buttonLabel}
                </PrimaryButton>
              );
            })()}
          </div>
        </div>
      </div>
      {/* Author details moved inside BookingTimeForm and BookingDatesForm */}
    </>
  );

  if (!isMobile) {
    return panelContent;
  }

  // RAS-2524/2525/2526: mobile sticky footer (always visible) + booking modal
  // (ModalInMobile - inline on desktop/unused here since isMobile gates this whole
  // branch, bottom-sheet on mobile). panelContent (title, PriceMaybe, the existing
  // BookingDatesForm/BookingTimeForm/etc ternary, CTA row, disclaimer lines) is
  // reused completely unchanged inside the modal - nothing above this point is
  // duplicated or reimplemented, only wrapped differently for mobile.
  // Prop names deliberately match the existing inline <PriceMaybe> call above
  // exactly (including its selectedCurrency prop, which PriceMaybe's own destructure
  // doesn't actually read - it reads currentUserCurrency instead) - this reuses the
  // same component with the same inputs, so the footer's price rendering is
  // identical to the panel's own, not a subtly different, newly-"corrected" version.
  // Manual QA: a customPackage whose modality disagrees with the listing's own
  // defaultPriceKey (e.g. a { minutes: 5 } package resolved for a perDayPrice listing
  // from a stale cart entry) must never reach the price pill - it rendered as
  // "$102.40 for 5 minutes" on a day-only listing. cartPrefillHelpers now classifies
  // prefills by defaultPriceKey so this shouldn't arise, and currentPackage above
  // already clamps which FORM renders; this clamps what the pill DISPLAYS, so all
  // three agree no matter which path produced the state.
  const footerPriceProps = {
    price: convertedAveragePrice,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    selectedCurrency: currentUserCurrency,
    selectedPackage: currentPackage,
    customPackage: displayCustomPackage,
    pricePackages,
    datesSelected,
    exchangeRatesReady,
    exchangeRates,
    // Keeps the footer's price + unit label ("/day" vs "/hour" vs "/minute") in step
    // with whichever category the modal's toggle currently has active.
    bookingCategory: effectiveCategory,
    isMultiCategory: availableCategories.length > 1,
  };

  const openBookingModal = () => setIsBookingModalOpen(true);

  // UI FIX 4 (manual QA): a bare <> Fragment here contributes 2 DOM-producing
  // children (ModalInMobile's rendered div + the sticky footer div) to whatever
  // renders this component, instead of the 1 child OrderPanel contributed before
  // this mobile branch existed. ListingPageCarousel.js's .orderColumnForProductLayout
  // reorders its own children with hardcoded nth-child->order CSS tuned for that old
  // 3-child shape (OrderPanel, About Owner, BookingSupport) - the extra child shifted
  // every sibling's index, which visually swapped About Owner and BookingSupport.
  // A single plain wrapping div restores the original 1-child shape instead of
  // re-tuning those fragile nth-child numbers. Deliberately NOT display:contents -
  // that was tried first, but display:contents has a real, documented cross-browser
  // bug (notably mobile Safari) where position:fixed descendants can fail to render
  // at all, which silently deleted the sticky footer entirely. A plain div has no
  // such risk: position:fixed only cares about the nearest ancestor with a transform/
  // filter/perspective/will-change, none of which this div (or ModalInMobile's own
  // wrapper) sets, so both children below still position exactly as if this div
  // weren't here.
  return (
    <div className={css.mobileOrderPanelRoot}>
      <ModalInMobile
        id="OrderPanelMobileBookingModal"
        isModalOpenOnMobile={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        className={css.mobileBookingModalRoot}
        containerClassName={css.mobileBookingModalContainer}
        contentClassName={css.mobileBookingModalContent}
        scrollLayerClassName={css.mobileBookingModalScrollLayer}
        // FIX 1 (manual QA): the shared Modal.close button defaults to a transparent
        // background (marketplaceModalCloseStyles in marketplaceDefaults.css, used by
        // every modal in the app) - too easy to miss and not a real 44x44 tap target
        // once padding is accounted for on some viewports. closeButtonClassName is
        // additive (see Modal.js) so this only changes this modal's own close button.
        closeButtonClassName={css.mobileBookingModalCloseButton}
        // Same reasoning as CartDrawer.js's own <Modal usePortal>: this component sits
        // deeply nested in the page's column/grid layout. ModalInMobile.js applies
        // usePortal for this entire mobile-only render branch (isMobileLayout, not
        // isOpen - see that file's own comment for why the distinction matters:
        // toggling it on open/close used to force-remount panelContent, discarding
        // resolved prefills), so desktop's "just an extra inline wrapper" behavior
        // (this branch never even renders on desktop, isMobile gates it above) is
        // unaffected.
        usePortal
      >
        {/* Booking modal only closes via Modal's own X button - no click-outside/backdrop
            dismiss and no drag-to-close, so accidental taps on the modal's empty space
            can't lose the user's in-progress booking selections. */}
        <div key={modalEpoch}>{panelContent}</div>
      </ModalInMobile>

      <div className={css.mobileStickyFooter} data-testid="mobile-booking-footer">
        <button
          type="button"
          className={css.mobileStickyFooterPrice}
          onClick={openBookingModal}
        >
          <PriceMaybe {...footerPriceProps} />
        </button>
        {/* Manual QA: the pre-selection state used a secondary-outline Button, which
            renders grey/hollow and read as disabled even though tapping it is the
            primary way into the booking flow. Both states now use the same filled
            PrimaryButton - only the label changes with progress, matching the price
            pill beside it (see .mobileStickyFooterPrice in this component's CSS). */}
        <PrimaryButton
          className={css.mobileStickyFooterButton}
          onClick={openBookingModal}
        >
          {datesSelected ? (
            <FormattedMessage id="OrderPanel.mobileFooterRentNow" />
          ) : (
            <FormattedMessage id="OrderPanel.mobileFooterSelectDates" />
          )}
        </PrimaryButton>
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  autoFocusBooking: false,
  isMobile: false,
  isCartDrawerOpen: false,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,
  autoFocusBooking: bool,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // RAS-2524/2525/2526: gates the mobile sticky footer + booking modal (ModalInMobile)
  // wrapper. Only ListingPageCarousel.js's mobile-only OrderPanel instance passes
  // true; the desktop sidebar instance and ListingWizardPreviewPanel.js's usage don't
  // pass it, so they keep today's inline-only render exactly as before.
  isMobile: bool,
  // Whether ListingPageCarousel.js's cart drawer is currently open - closes the mobile
  // booking modal in response (see the effect above). Only meaningful alongside isMobile.
  isCartDrawerOpen: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
